import { AfterContentInit, AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AnimatedDelayDirective, AnimatedDirective } from '../../../shared/directives/animated.directive';
import { AppLoadingTableStateComponent } from '../../../shared/table/loading-table-state/loading-table-state.component';
import { AppTableDirective, AppTableRowDirective, EmptyCellDirective, JustifyRightCellDirective } from '../../../shared/table/table.directive';
import { AsyncPipe, NgIf } from '@angular/common';
import { MatHeaderRow, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { LegfiDatePipeModule } from '../../../../../pipes/format/legfi-date.module';
import { MatButtonModule } from '@angular/material/button';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { PreventClickDirective } from '../../../shared/directives/prevent-click.directive';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FormSubmissionApproval } from '../../../../../models/entities/forms/form-submission-approval';
import { ColumnMap } from '../../../../../models/entities/column-map';
import { RequestFormService } from '../../../../../services/forms/request-form.service';
import { FilteringService } from '../../../../../services/filtering.service';
import { HostListenerService } from '../../../../../services/host-listener.service';
import { GrowlerService } from '../../../../../services/growler.service';
import { map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'app-request-form-submission-approvals',
    templateUrl: './request-form-submission-approvals.component.html',
    standalone: true,
    styleUrls: ['./request-form-submission-approvals.component.scss'],
    imports: [
        AnimatedDirective,
        AppTableDirective,
        AsyncPipe,
        MatTableModule,
        MatSortModule,
        MatIconModule,
        NgIf,
        LegfiDatePipeModule,
        EmptyCellDirective,
        JustifyRightCellDirective,
        MatButtonModule,
        ButtonDirective,
        PreventClickDirective,
        MatProgressBarModule,
        AppTableRowDirective,
        AnimatedDelayDirective,
        AppLoadingTableStateComponent,
    ],
})
export class RequestFormSubmissionApprovalsComponent implements OnChanges, AfterContentInit, AfterViewInit
{
    @Input() formSubmissionId: number;
    @Output() approvalsLoaded: EventEmitter<FormSubmissionApproval[]> = new EventEmitter<FormSubmissionApproval[]>();

    @ViewChild(MatSort) matSort: MatSort;
    @ViewChild(MatHeaderRow, {read: ElementRef}) tableHeaderRef: ElementRef;

    dataSource: MatTableDataSource<FormSubmissionApproval> = new MatTableDataSource<FormSubmissionApproval>([]);
    columnMap: ColumnMap[] = [
        {key: 'mobile', title: 'Approver', isMobile: true},
        {key: 'name', title: 'Approver'},
        {key: 'status', title: 'Status'},
        {key: 'createdAt', title: 'Date'},
        {key: 'actions', title: 'Actions'},
    ];
    isLoadingApprovals = true;
    rowsAnimated = true;
    emailingApproverId = -1;

    constructor(
            private _requestFormService: RequestFormService,
            private _filteringService: FilteringService,
            private _host: HostListenerService,
            private _growler: GrowlerService,
            private _cd: ChangeDetectorRef,
    ) {
    }

    get columnKeys$() {
        return this._host.isMobileMode().pipe(map((isMobile) => {
            let columns: string[];
            if (isMobile) {
                columns = this.columnMap.filter((column) => !!column.isMobile).map((column) => column.key);
            } else {
                columns = this.columnMap.filter((column) => !column.isMobile).map((column) => column.key);
            }
            return columns;
        }));
    }

    get mobileMode$() {
        return this._host.isMobileMode();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.hasOwnProperty('formSubmissionId') && this.formSubmissionId) {
            this._requestFormService.getMappedApproversAndApprovals(this.formSubmissionId).pipe(
                    untilDestroyed(this),
            ).subscribe({
                next: (approvals) => {
                    this.dataSource.data = approvals;
                    this.rowsAnimated = false;
                    this.isLoadingApprovals = false;
                    this.approvalsLoaded.emit(approvals);
                }, error: () => this._growler.oops('Unable to load approvals for this form submission'),
            });
        }
    }

    ngAfterContentInit() {
        this._cd.detectChanges();
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.matSort;
        this.dataSource.sortingDataAccessor = (
                data,
                header,
        ) => {
            switch (header) {
                case 'name':
                    return data.membership?.profile.familyName;
                case 'createdAt':
                    return data.createdAt?.unix();
                default:
                    return this._filteringService.normalizeMixedDataValueForSorting(
                            this._filteringService.extractPath(header, data),
                    );
            }
        };
    }

    notifyApprover(formApproverId: number) {
        this.emailingApproverId = formApproverId;
        this._requestFormService.sendApprovalReminder(this.formSubmissionId, formApproverId).pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this._growler.success('Success', 'Reminder email has been sent to approver');
                this.emailingApproverId = -1;
            }, error: () => this._growler.oops('Unable to send reminder email to approver'),
        });
    }
}
